import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileCheck, Plus, Trash2, Save, Loader2, Download } from 'lucide-react';

export const ContractsTab = ({ bookings = [] }) => {
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newOb, setNewOb] = useState('');

  useEffect(() => {
    adminAPI.getContractObligations()
      .then(r => setObligations(r.data.obligations))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateContractObligations({ obligations });
      toast.success('Obligations mises à jour');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const addObligation = () => {
    if (!newOb.trim()) return;
    setObligations(prev => [...prev, newOb.trim()]);
    setNewOb('');
  };

  const removeObligation = (index) => {
    setObligations(prev => prev.filter((_, i) => i !== index));
  };

  const signedBookings = bookings.filter(b => b.contract_signed);

  return (
    <div className="space-y-6" data-testid="contracts-tab">
      {/* Obligations management */}
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6">
        <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-[#00FF00]" /> Obligations du contrat
        </h3>
        <p className="text-slate-500 text-xs mb-4">Les clients doivent accepter toutes ces obligations avant de participer à une activité.</p>

        {loading ? (
          <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto" /></div>
        ) : (
          <div className="space-y-2 mb-4">
            {obligations.map((ob, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#0A0A0A] rounded-lg p-3 border border-[#2A2A2A]" data-testid={`obligation-${i}`}>
                <span className="text-[#00FF00] font-bold text-xs w-6">{i + 1}.</span>
                <span className="text-slate-300 text-sm flex-1">{ob}</span>
                <button onClick={() => removeObligation(i)} className="text-red-400/50 hover:text-red-400 transition-colors" data-testid={`obligation-delete-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-4">
          <Input value={newOb} onChange={e => setNewOb(e.target.value)} placeholder="Nouvelle obligation..." onKeyDown={e => e.key === 'Enter' && addObligation()}
            className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="obligation-new-input" />
          <Button onClick={addObligation} variant="outline" className="border-[#2A2A2A] text-slate-400 hover:text-white hover:bg-[#2A2A2A]" data-testid="obligation-add-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="obligations-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>

      {/* Signed contracts overview */}
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6">
        <h3 className="font-heading text-lg font-bold text-white mb-4">Contrats signés</h3>
        {signedBookings.length === 0 ? (
          <p className="text-slate-500 text-center py-6 text-sm">Aucun contrat signé</p>
        ) : (
          <div className="space-y-2">
            {signedBookings.slice(0, 20).map(b => (
              <div key={b.id} className="flex items-center justify-between bg-[#0A0A0A] rounded-xl p-3 border border-[#2A2A2A]" data-testid={`contract-booking-${b.id}`}>
                <div className="flex items-center gap-3">
                  <FileCheck className="w-4 h-4 text-[#00FF00]" />
                  <div>
                    <span className="text-white text-sm font-medium">{b.client_name}</span>
                    <span className="text-slate-500 text-xs ml-2">{b.activity_name} — {b.date} {b.time_slot}</span>
                  </div>
                  <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 text-xs">Signé</Badge>
                </div>
                <button onClick={() => window.open(adminAPI.getBookingContractPdf(b.id), '_blank')}
                  className="flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium bg-[#00FF00]/10 text-[#00FF00] hover:bg-[#00FF00]/20 transition-colors"
                  data-testid={`contract-pdf-${b.id}`}>
                  <Download className="w-3 h-3" /> PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
